import apiClient from '@/services/api'

const DRAFTS_API_URL = '/api/pbj/audit-drafts'
let latestDraftListRequestId = 0
let latestDraftRequestId = 0
const activeDraftMutations = new Map()

const assertNoApiError = data => {
  if (data?.error_code && String(data.error_code) !== '0') {
    throw new Error(data.msg || 'PBJ Audit request could not be completed')
  }
}

const getApiErrorMessage = error => {
  const responseData = error?.response?.data

  if (responseData && typeof responseData === 'object') {
    const validationMessage = Object.values(responseData).reduce((message, value) => {
      if (message || !Array.isArray(value)) {
        return message
      }

      return value.find(item => typeof item === 'string' && item)
    }, undefined)

    if (validationMessage) {
      return validationMessage
    }
  }

  const conflictMessages = Array.isArray(responseData?.conflicts)
    ? responseData.conflicts.map(conflict => conflict?.message).filter(Boolean)
    : []

  return (
    [responseData?.message, ...conflictMessages].filter(Boolean).join(' ') ||
    responseData?.detail ||
    responseData?.msg ||
    error?.message ||
    'PBJ Audit request could not be completed'
  )
}

const normalizeDraftIds = draftIds => [...new Set((draftIds ?? []).filter(id => Number.isInteger(id) && id > 0))]

const requestDraftXmlExport = async ({
  draftIds,
  facility_id,
  state_code,
  report_quater,
  fiscal_year,
  useBulkExport
}) => {
  const normalizedDraftIds = normalizeDraftIds(draftIds)

  if (!normalizedDraftIds.length) {
    throw new Error('Select at least one PBJ Audit draft')
  }

  const exportFields = {
    state_code,
    report_quater,
    fiscal_year,
    ...(facility_id ? { facility_id } : {})
  }

  const useSingleExport = normalizedDraftIds.length === 1 && !useBulkExport
  const { data } = useSingleExport
    ? await apiClient.post(`${DRAFTS_API_URL}/${normalizedDraftIds[0]}/export/xml`, exportFields)
    : await apiClient.post(`${DRAFTS_API_URL}/export/xml`, {
        ...exportFields,
        draft_ids: normalizedDraftIds
      })

  assertNoApiError(data)

  if (useSingleExport) {
    return data?.xml_report_link ? [data.xml_report_link] : []
  }

  return Array.isArray(data?.exports) ? data.exports.map(item => item?.xml_report_link).filter(Boolean) : []
}

const commitProjection = ({ commit, rootState }, projection) => {
  const projectId = rootState.applicationState.currentProject?.id

  if (!projectId || !projection?.draft?.id) {
    throw new Error('The PBJ audit draft response is incomplete')
  }

  commit('SET_ACTIVE_DRAFT', { draft: projection, projectId })
  return projection
}

const runDraftMutation = async (context, { draftId, request }) => {
  const { commit, dispatch, rootState, state } = context
  const projectId = rootState.applicationState.currentProject?.id

  if (!projectId) {
    throw new Error('Select a company before changing a PBJ audit draft')
  }

  if (activeDraftMutations.has(projectId) || (!activeDraftMutations.size && state.draftMutating)) {
    throw new Error('Another PBJ audit draft change is already in progress')
  }

  const mutationToken = Symbol('pbj-audit-draft-mutation')
  activeDraftMutations.set(projectId, mutationToken)
  latestDraftRequestId += 1
  commit('SET_DRAFT_LOADING', false)

  commit('SET_DRAFT_MUTATING', true)

  try {
    const { data } = await request()
    assertNoApiError(data)

    if (rootState.applicationState.currentProject?.id !== projectId) {
      throw new Error('The company changed while the PBJ audit draft was being updated')
    }

    return commitProjection(context, data)
  } catch (error) {
    if (error?.response?.status === 409 && rootState.applicationState.currentProject?.id === projectId) {
      try {
        await dispatch('fetchDraft', draftId)
      } catch {
        // Preserve the mutation error; the workspace still exposes manual refresh.
      }
    }

    throw new Error(getApiErrorMessage(error))
  } finally {
    if (activeDraftMutations.get(projectId) === mutationToken) {
      activeDraftMutations.delete(projectId)
      if (rootState.applicationState.currentProject?.id === projectId) {
        commit('SET_DRAFT_MUTATING', false)
      }
    }
  }
}

export default {
  async fetchDrafts({ commit, rootState, state }) {
    const requestId = ++latestDraftListRequestId
    const projectId = rootState.applicationState.currentProject?.id

    if (!projectId) {
      commit('SET_PROJECT_CONTEXT', null)
      commit('SET_DRAFTS_LOADING', false)
      return []
    }

    if (state.projectId !== projectId) {
      commit('SET_PROJECT_CONTEXT', projectId)
    }

    commit('SET_DRAFTS_LOADING', true)

    try {
      const { data } = await apiClient.get(DRAFTS_API_URL, {
        cancelPreviousRequests: true,
        requestGroup: 'pbj-audit-draft-list'
      })

      assertNoApiError(data)

      if (requestId !== latestDraftListRequestId || rootState.applicationState.currentProject?.id !== projectId) {
        return []
      }

      const drafts = Array.isArray(data?.drafts) ? data.drafts : []
      commit('SET_DRAFTS', { drafts, projectId })

      return drafts
    } catch (error) {
      if (error?.code === 'ERR_CANCELED') {
        return undefined
      }

      throw new Error(getApiErrorMessage(error))
    } finally {
      if (requestId === latestDraftListRequestId && rootState.applicationState.currentProject?.id === projectId) {
        commit('SET_DRAFTS_LOADING', false)
      }
    }
  },

  async exportDraftXml(_, payload) {
    try {
      return await requestDraftXmlExport(payload)
    } catch (error) {
      throw new Error(getApiErrorMessage(error))
    }
  },

  async exportDraftsXmlBulk(_, payload) {
    try {
      return await requestDraftXmlExport({ ...payload, useBulkExport: true })
    } catch (error) {
      throw new Error(getApiErrorMessage(error))
    }
  },

  async createDraft({ commit, rootState, state }, payload) {
    const projectId = rootState.applicationState.currentProject?.id

    if (!projectId) {
      throw new Error('Select a company before creating a PBJ audit draft')
    }

    if (state.projectId !== projectId) {
      commit('SET_PROJECT_CONTEXT', projectId)
    }

    commit('SET_DRAFT_CREATING', true)

    try {
      const { data } = await apiClient.post(DRAFTS_API_URL, payload)

      assertNoApiError(data)

      if (rootState.applicationState.currentProject?.id !== projectId) {
        throw new Error('The company changed while the PBJ audit draft was being created')
      }

      if (!data?.draft?.id) {
        throw new Error('The created PBJ audit draft is missing its ID')
      }

      commit('SET_ACTIVE_DRAFT', { draft: data, projectId })

      return data
    } catch (error) {
      throw new Error(getApiErrorMessage(error))
    } finally {
      commit('SET_DRAFT_CREATING', false)
    }
  },

  async deleteDraft({ commit }, draftId) {
    commit('SET_DRAFT_DELETING', draftId)

    try {
      const response = await apiClient.delete(`${DRAFTS_API_URL}/${draftId}`)
      assertNoApiError(response?.data)
      commit('REMOVE_DRAFT', draftId)
    } catch (error) {
      throw new Error(getApiErrorMessage(error))
    } finally {
      commit('SET_DRAFT_DELETING', null)
    }
  },

  adjustDraftShift(context, { draftId, revision, draftShiftId, values }) {
    return runDraftMutation(context, {
      draftId,
      request: () =>
        apiClient.post(`${DRAFTS_API_URL}/${draftId}/operations/adjust`, {
          revision,
          draft_shift_id: draftShiftId,
          ...values
        })
    })
  },

  createDraftShift(context, { draftId, revision, values }) {
    return runDraftMutation(context, {
      draftId,
      request: () =>
        apiClient.post(`${DRAFTS_API_URL}/${draftId}/operations/create`, {
          revision,
          ...values
        })
    })
  },

  deleteDraftShift(context, { draftId, revision, draftShiftId }) {
    return runDraftMutation(context, {
      draftId,
      request: () =>
        apiClient.post(`${DRAFTS_API_URL}/${draftId}/operations/delete`, {
          revision,
          draft_shift_id: draftShiftId
        })
    })
  },

  rollbackDraftOperation(context, { draftId, revision, operationId }) {
    return runDraftMutation(context, {
      draftId,
      request: () =>
        apiClient.delete(`${DRAFTS_API_URL}/${draftId}/operations/${operationId}`, {
          data: { revision }
        })
    })
  },

  applyDraft(context, { draftId, revision }) {
    return runDraftMutation(context, {
      draftId,
      request: () => apiClient.post(`${DRAFTS_API_URL}/${draftId}/apply`, { revision })
    })
  },

  async fetchDraft({ commit, rootState, state }, draftId) {
    const requestId = ++latestDraftRequestId
    const projectId = rootState.applicationState.currentProject?.id

    if (!projectId) {
      commit('SET_PROJECT_CONTEXT', null)
      return null
    }

    if (state.projectId !== projectId) {
      commit('SET_PROJECT_CONTEXT', projectId)
    }

    commit('SET_DRAFT_LOADING', true)

    try {
      const { data } = await apiClient.get(`${DRAFTS_API_URL}/${draftId}`, {
        cancelPreviousRequests: true,
        requestGroup: 'pbj-audit-draft'
      })

      assertNoApiError(data)

      if (requestId !== latestDraftRequestId || rootState.applicationState.currentProject?.id !== projectId) {
        return null
      }

      commit('SET_ACTIVE_DRAFT', { draft: data, projectId })

      return data
    } catch (error) {
      if (error?.code === 'ERR_CANCELED') {
        return null
      }

      throw new Error(getApiErrorMessage(error))
    } finally {
      if (requestId === latestDraftRequestId && rootState.applicationState.currentProject?.id === projectId) {
        commit('SET_DRAFT_LOADING', false)
      }
    }
  },

  clearActiveDraft({ commit, rootState }) {
    latestDraftRequestId += 1
    commit('SET_DRAFT_LOADING', false)
    commit('SET_ACTIVE_DRAFT', {
      draft: null,
      projectId: rootState.applicationState.currentProject?.id ?? null
    })
  }
}
